import { db } from "../firebase/firebaseConfig";
import {
  collection,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  runTransaction,
  addDoc,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import {
  Song,
  Collections,
  Artist,
  Short,
  HomebrewPlaylist,
  MakingRequest,
} from "../types/type";
import {
  generateArtistId,
  generateHomebrewPlaylistId,
  generateShortId,
  generateSongId,
} from "./fetch.api";
import { urlFooter, urlHeader } from "../module/constant";
import { generateSearchKeywordList } from "../module/search/generate";
import { updateUpdateDate } from "./update.api";

interface AddSongParams {
  artistId: string;
  artistName: string;
  title: string;
  thumbnailUrl: string;
  youtubeId: string;
  tagList: string[];
  isAI: boolean;
  makerUserId: string;
  tag: { genreList: string[]; [key: string]: any };
  url?: string;
}

export const addNewSong = async ({
  artistId,
  artistName,
  title,
  thumbnailUrl,
  youtubeId,
  tagList,
  isAI,
  makerUserId,
  tag,
  url,
}: AddSongParams): Promise<string> => {
  console.log("0");
  const songCollectionRef = collection(db, Collections.SONG);
  const artistDocRef = doc(db, Collections.ARTIST, artistId);

  try {
    // Generate new songId
    const newSongId = await generateSongId();
    console.log("addNewSong songId : ", newSongId);

    const finalThumbnailUrl = thumbnailUrl
      ? thumbnailUrl
      : `https://firebasestorage.googleapis.com/v0/b/homebrew-prod.appspot.com/o/artwork%2F${newSongId}.jpg?alt=media`;

    const searchKeywordList = generateSearchKeywordList(title);

    // Create new song data
    const newSong: Song = {
      songId: newSongId,
      artistIdList: [artistId],
      artist: artistName,
      artistName: artistName,
      title,
      thumbnailUrl: finalThumbnailUrl,
      artwork: finalThumbnailUrl,
      url: url || `${urlHeader}${newSongId}${urlFooter}`,
      youtubeId,
      viewCount: 0,
      chartRank: 0,
      likeUserIdList: [],
      isAI,
      makerUserId,
      madeAt: serverTimestamp(),
      tag: {
        genreList: tag.genreList,
        tagList: tagList,
      },
      lyricistNameList: [],
      composerNameList: [],
      arrangerNameList: [],
      isKomcaManaged: true,
      viewCount30sec: 0,
      viewCount50sec: 0,
      viewCount60sec: 0,
      viewCount90sec: 0,
      tagList,
      originalSongId: "",
      isOriginalSong: false,
      searchKeywordList,
    };

    console.log("1");
    // Add new song to the song collection
    const songDocRef = doc(db, Collections.SONG, newSongId);
    await setDoc(songDocRef, newSong);

    console.log("2");
    // Update artist document with new song ID
    const artistDocSnap = await getDoc(artistDocRef);
    if (artistDocSnap.exists()) {
      const artistData = artistDocSnap.data() as Artist;
      const updatedSongIdList = [...artistData.songIdList, newSongId];
      await updateDoc(artistDocRef, {
        songIdList: updatedSongIdList,
      });
      alert(
        `[노래 추가 성공]\nsongId : ${newSongId}\n제목 : ${title}\n가수 : ${artistName}`
      );
      updateUpdateDate({ documentId: "Song", field: "updateDate" });
      updateUpdateDate({ documentId: "Artist", field: "allUpdateDate" });
      return newSongId;
    } else {
      throw new Error("Artist document does not exist");
    }
  } catch (error) {
    console.error("Error adding new song: ", error);
    throw error;
  }
};

interface AddShortParams {
  artistId: string;
  artistName: string;
  title: string;
  thumbnailUrl: string;
  youtubeId: string;
  isAI: boolean;
  makerUserId: string;
  originalSongId: string;
  tag: {
    genreList: string[];
    themeList: string[];
    tagList: string[];
    [key: string]: any;
  };
}

export const addNewShort = async ({
  artistId,
  artistName,
  title,
  thumbnailUrl,
  youtubeId,
  isAI,
  makerUserId,
  tag,
  originalSongId,
}: AddShortParams): Promise<void> => {
  console.log("0");
  console.log("artistId : ", artistId);
  const shortCollectionRef = collection(db, Collections.SHORT);
  const artistDocRef = doc(db, Collections.ARTIST, artistId);

  try {
    // Generate new shortId
    const newShortId = await generateShortId();

    // Create new short data
    const newShort: Short = {
      shortId: newShortId,
      artistIdList: [artistId],
      artistName,
      title,
      thumbnailUrl,
      youtubeId,
      viewCount: 0,
      viewCount30sec: 0,
      chartRank: 0,
      likeUserIdList: [],
      isAI,
      makerUserId,
      madeAt: serverTimestamp(),
      originalSongId: originalSongId,
      tag: {
        genreList: tag.genreList,
        themeList: tag.themeList,
        tagList: tag.tagList,
      },
    };

    // Add new short to the short collection
    const shortDocRef = doc(db, Collections.SHORT, newShortId);
    await setDoc(shortDocRef, newShort);

    console.log("2");
    // Update artist document with new short ID
    const artistDocSnap = await getDoc(artistDocRef);
    if (artistDocSnap.exists()) {
      const artistData = artistDocSnap.data() as Artist;
      const updatedShortIdList = [...artistData.shortIdList, newShortId];
      await updateDoc(artistDocRef, {
        shortIdList: updatedShortIdList,
      });
      alert(
        `[쇼츠 추가 성공]\nshortId : ${newShortId}\n제목 : ${title}\n가수 : ${artistName}`
      );
      updateUpdateDate({ documentId: "Short", field: "updateDate" });
      updateUpdateDate({ documentId: "Artist", field: "allUpdateDate" });
    } else {
      throw new Error("Artist document does not exist");
    }
  } catch (error) {
    console.error("Error adding new short: ", error);
    throw error;
  }
};

interface AddHomebrewPlaylistParams {
  playlistId: string;
  artistIdList: string[];
  artistName: string;
  title: string;
  subTitle: string;
  imageUrl: string;
  songList: Song[];
}

export const addNewHomebrewPlaylist = async ({
  playlistId,
  artistIdList,
  artistName,
  title,
  subTitle,
  imageUrl,
  songList,
}: AddHomebrewPlaylistParams): Promise<void> => {
  const playlistCollectionRef = collection(db, "HomebrewPlaylist");
  const artistDocRef = doc(db, Collections.ARTIST, artistIdList[0]);

  try {
    // Generate new playlistId
    const newPlaylistId = await generateHomebrewPlaylistId();

    await runTransaction(db, async (transaction) => {
      // Create new playlist data
      const newPlaylist: HomebrewPlaylist = {
        playlistId: newPlaylistId,
        artistIdList: artistIdList,
        artistName: artistName,
        title,
        subTitle,
        imageUrl,
        songList,
        tag: {
          genreList: [],
          tagList: [],
        },
      };

      const artistDocSnap = await transaction.get(artistDocRef);
      // Add new playlist to the playlist collection
      const playlistDocRef = doc(playlistCollectionRef, newPlaylistId);
      transaction.set(playlistDocRef, newPlaylist);

      // Update artist document with new playlist ID

      if (artistDocSnap.exists()) {
        const artistData = artistDocSnap.data() as Artist;
        const updatedHomebrewPlaylistIdList = [
          ...artistData.homebrewPlaylistIdList,
          newPlaylistId,
        ];
        transaction.update(artistDocRef, {
          homebrewPlaylistIdList: updatedHomebrewPlaylistIdList,
        });
        alert(
          `[플리 추가 성공]\nplaylistId : ${newPlaylistId}\n제목 : ${title}\n가수 : ${artistName}`
        );
      } else {
        throw new Error("Artist document does not exist");
      }
    });

    console.log("Playlist and artist documents updated successfully");
  } catch (error) {
    console.error("Error adding new playlist: ", error);
    throw error;
  }
};

interface AddArtistParams {
  artistName: string;
  artistImageUrl: string;
  artistDetailImageUrl: string;
  followerIdList: string[];
  songIdList: string[];
  shortIdList: string[];
  homebrewPlaylistIdList: string[];
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
  isMale: boolean;
  modelName: string;
}

export const addNewArtist = async ({
  artistName,
  artistImageUrl,
  artistDetailImageUrl,
  followerIdList,
  songIdList,
  shortIdList,
  homebrewPlaylistIdList,
  tag,
  isMale,
  modelName,
}: AddArtistParams): Promise<void> => {
  try {
    // Generate a new artistId
    const newArtistId = await generateArtistId();
    console.log("addNewArtist artistId : ", newArtistId);

    const searchKeywordList = generateSearchKeywordList(artistName);

    // Create new artist data
    const newArtist: Artist = {
      artistId: newArtistId,
      artistName,
      artistImageUrl,
      artistDetailImageUrl,
      chartRank: 0, // Assuming a default value of 0 for chartRank
      followerIdList,
      songIdList,
      shortIdList,
      homebrewPlaylistIdList,
      tag: {
        genreList: tag.genreList,
        tagList: tag.tagList,
      },
      searchKeywordList,
      isMale,
      modelName,
    };

    // Add new artist to the artist collection
    const artistCollectionRef = collection(db, Collections.ARTIST);
    const artistDocRef = doc(artistCollectionRef, newArtistId);
    await setDoc(artistDocRef, newArtist);

    alert(
      `[아티스트 추가 성공]\nartistId : ${newArtistId}\n아티스트 : ${artistName}`
    );

    // Update the global update date for the artist collection
    updateUpdateDate({ documentId: "Artist", field: "updateDate" });
  } catch (error) {
    console.error("Error adding new artist: ", error);
    throw error;
  }
};

export const addMakingDocument = async (makingData: MakingRequest) => {
  try {
    const makingRequestCollectionRef = collection(
      db,
      Collections.MAKING_REQUEST
    );
    const makingRequestDocRef = doc(
      makingRequestCollectionRef,
      makingData.songRequestId
    );
    await setDoc(makingRequestDocRef, makingData);
  } catch (error) {
    console.error("Error adding document: ", error);
    throw error;
  }
};
